import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Logger, LoggingService } from 'ionic-logging-service';
import { EMPTY, Observable } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { DialogService } from '../dialog/dialog.service';
import { ErrorService } from '../error/error.service';
import { LogsService } from '../logs/logs.service';
import { NetworkService } from '../network/network.service';
import { SanitizationService } from '../sanitization/sanitization.service';
import { IDocumentUploadSession, UploadState } from './models/document-upload-session.model';

const ACCEPTED_EXTENSIONS = ['.pdf'];

const MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024;

@Injectable({
  providedIn: 'root'
})
export class DocumentPrintService {

  public uploadState = signal<UploadState>('idle');

  private logger: Logger;

  constructor(
    private httpClient: HttpClient,
    private dialogService: DialogService,
    private errorService: ErrorService,
    private loggingService: LoggingService,
    private logsService: LogsService,
    private networkService: NetworkService,
    private sanitizationService: SanitizationService,
  ) {
    this.logger = this.loggingService.getLogger("[DocumentPrintService]");
    const methodName = "ctor";
    this.logger.entry(methodName);
  }

  // ############### VALIDATION ###############

  public validateFile(file: File): string | null {
    const methodName = "validateFile";
    this.logger.info(methodName);

    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
      return 'UnsupportedFileType';
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return 'FileTooLarge';
    }
    return null;
  }

  // ############### UPLOAD PIPELINE ###############

  public uploadDocument(
    printJobsUrl: string,
    printerUrl: string,
    queueUrl: string,
    file: File
  ): Observable<any> {
    const methodName = "uploadDocument";
    this.logger.info(methodName);

    if (!this.networkService.hasInternetAccess()) {
      this.dialogService.showAlertDialog('NoAccessInternet').subscribe();
      return EMPTY;
    }

    this.uploadState.set('initiating');

    return this.initiateUpload(printJobsUrl, printerUrl, queueUrl, file.name).pipe(
      switchMap((session: IDocumentUploadSession) => {
        this.uploadState.set('uploading');
        return this.uploadFile(session.uploadLink, session.uploadHeaders, file).pipe(
          switchMap(() => {
            this.uploadState.set('confirming');
            return this.confirmUpload(session.confirmLink);
          })
        );
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 0) {
          const isOnline = this.networkService.hasInternetAccess();
          this.logsService.logErrorData(error, 'POST/PUT', methodName);
          this.logger.warn(methodName, 'status=0 received, networkService.hasInternetAccess()=' + isOnline);

          this.uploadState.set('idle');
          if (!isOnline) {
            this.dialogService.showAlertDialog('NoAccessInternet').subscribe();
          } else {
            this.dialogService.showAlertDialog('UploadFailed').subscribe();
          }
          return EMPTY;
        }
        this.uploadState.set('error');
        return this.errorService.handleHttpClientResponseError(error, 'POST/PUT', methodName);
      })
    );
  }

  private initiateUpload(
    printJobsUrl: string,
    printerUrl: string,
    queueUrl: string,
    jobName: string
  ): Observable<IDocumentUploadSession> {
    const methodName = "initiateUpload";
    this.logger.info(methodName);

    const safeJobName = this.sanitizationService.sanitizeTextInput(jobName);
    const body = new HttpParams()
      .set('printer', printerUrl)
      .set('queue', queueUrl)
      .set('jobName', safeJobName);

    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    return this.httpClient.post<any>(printJobsUrl, body.toString(), { headers }).pipe(
      map(response => {
        const uploadLink = this.sanitizationService.validateStorageUploadUrl(response._links['px:upload']?.href);
        const confirmLink = this.sanitizationService.validateApiUrl(response._links['px:uploadCompleted']?.href);

        if (!uploadLink) {
          throw new Error('Invalid or missing upload URL in API response');
        }
        if (!confirmLink) {
          throw new Error('Invalid or missing confirm URL in API response');
        }

        const session: IDocumentUploadSession = {
          uploadLink,
          uploadHeaders: response.uploadHeaders,
          confirmLink
        };
        return session;
      })
    );
  }

  private uploadFile(
    uploadLink: string,
    uploadHeaders: Record<string, string>,
    file: File
  ): Observable<void> {
    const methodName = "uploadFile";
    this.logger.info(methodName, 'PUT starting, file=' + this.sanitizationService.sanitizeLogOutput(file.name) + ' size=' + file.size);

    const headers = new HttpHeaders(uploadHeaders);
    return this.httpClient.put<void>(uploadLink, file, { headers }).pipe(
      tap(() => this.logger.info(methodName, 'PUT completed, file=' + this.sanitizationService.sanitizeLogOutput(file.name)))
    );
  }

  private confirmUpload(confirmLink: string): Observable<any> {
    const methodName = "confirmUpload";
    this.logger.info(methodName);

    return this.httpClient.post<any>(confirmLink, {});
  }

  // ############### RESET ###############

  public reset(): void {
    const methodName = "reset";
    this.logger.info(methodName);
    this.uploadState.set('idle');
  }
}
